/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Button, ButtonGroup, ModalActions, StyledModal } from 'sarsaparilla';
import { useWizardContext } from './context/WizardContext';
import ScrollToReadMore from '../shared/ScrollToReadMore';
import { preloadWizardIcons } from './icons/wizardIcons';
import DisclaimerStep from './steps/DisclaimerStep';
import ResidencyStep from './steps/ResidencyStep';
import AttributeSelectionStep from './steps/AttributeSelectionStep';
import VisitFrequencyStep from './steps/VisitFrequencyStep';
import { WizardResults } from './results/WizardResults';
import type { UserAttribute, VisitFrequency, WizardStep } from './types/wizard.types';

import './WizardModal.scss';

const STEP_SUBTITLES: Record<string, string> = {
    disclaimer:
        'Before we begin, here are a few things to know about America the Beautiful Passes.',
    residency: 'Are you a U.S. citizen or resident?',
    attributes: 'Which of the following apply to you?  Select all that apply.',
    'visit-frequency':
        'How many federal recreational locations do you plan to visit in the next 12 months?',
};

function getResultsSubtitle(count: number): string {
    if (count === 1) {
        return 'Based on your answers, this pass may be the best option for you.';
    }
    return 'Based on your answers, here are a few options for you to consider.';
}

function getNextButtonGaTrackingValue(
    step: WizardStep,
    residentAnswer: boolean | null,
    selectedAttributes: UserAttribute[],
    frequencyAnswer: VisitFrequency | null
): string {
    switch (step) {
        case 'disclaimer':
            return 'Continue';
        case 'residency':
            if (residentAnswer === null) return 'Next';
            return `Next|residency:${residentAnswer ? 'yes' : 'no'}`;
        case 'attributes': {
            const attrs = [...selectedAttributes].sort().join(',');
            return attrs ? `Next|attributes:${attrs}` : 'Next';
        }
        case 'visit-frequency':
            return frequencyAnswer ? `Next|visit-frequency:${frequencyAnswer}` : 'Next';
        default:
            return 'Next';
    }
}

interface WizardModalProps {
    shouldCloseOnOverlayClick: boolean;
}

function WizardModal({ shouldCloseOnOverlayClick = true }: WizardModalProps) {
    const {
        isOpen,
        showCloseButton,
        currentStep,
        canProceed,
        recommendations,
        flags,
        residentAnswer,
        selectedAttributes,
        frequencyAnswer,
        closeWizard,
        nextStep,
        previousStep,
    } = useWizardContext();

    // Preload all wizard SVG illustrations on first open to prevent flicker
    const hasPreloaded = useRef(false);
    useEffect(() => {
        if (isOpen && !hasPreloaded.current) {
            hasPreloaded.current = true;
            preloadWizardIcons();
        }
    }, [isOpen]);

    // Scroll step pane to top on step change; reset overlay when modal filled viewport
    const stepContainerRef = useRef<HTMLDivElement | null>(null);
    const [stepScrollEl, setStepScrollEl] = useState<HTMLDivElement | null>(null);

    // Keep the ref (used below) and the state (fed to ScrollToReadMore) in sync
    const setStepContainer = useCallback((el: HTMLDivElement | null) => {
        stepContainerRef.current = el;
        setStepScrollEl(el);
    }, []);

    useEffect(() => {
        if (!isOpen || !stepContainerRef.current) return;

        requestAnimationFrame(() => {
            const root = stepContainerRef.current;
            if (!root) return;

            root.scrollTop = 0;

            const modalOverlay = root.closest(
                '.sarsa-modal-overlay'
            ) as HTMLElement | null;
            if (modalOverlay) {
                modalOverlay.scrollTop = 0;
            }

            stepContainerRef.current?.focus();
        });
    }, [currentStep, isOpen]);

    if (!isOpen) return null;

    const isFirstStep =
        currentStep === (flags.disclaimerEnabled ? 'disclaimer' : 'residency');
    const isResults = currentStep === 'results';

    const nextGaTrackingValue = getNextButtonGaTrackingValue(
        currentStep,
        residentAnswer,
        selectedAttributes,
        frequencyAnswer
    );

    const subtitle = isResults
        ? getResultsSubtitle(recommendations.length)
        : STEP_SUBTITLES[currentStep] || '';

    const renderStepContent = () => {
        switch (currentStep) {
            case 'disclaimer':
                return <DisclaimerStep />;
            case 'residency':
                return <ResidencyStep />;
            case 'attributes':
                return <AttributeSelectionStep />;
            case 'visit-frequency':
                return <VisitFrequencyStep />;
            case 'results':
                return <WizardResults />;
            default:
                return null;
        }
    };

    return (
        <StyledModal
            isOpen={isOpen}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            onRequestClose={closeWizard}
            heading="Help Me Choose a Pass"
            size="lg"
            shouldShowCloseButton={showCloseButton}
            contentClassName="wizard-modal"
        >
            {subtitle && (
                <p
                    className={cx('wizard-modal__subtitle', {
                        'wizard-modal__subtitle--attributes':
                            currentStep === 'attributes',
                    })}
                >
                    {subtitle}
                </p>
            )}
            <div
                className="wizard-modal__step-content"
                ref={setStepContainer}
                tabIndex={-1}
                aria-label={subtitle}
            >
                {renderStepContent()}
                <ScrollToReadMore
                    key={currentStep}
                    scrollElement={stepScrollEl}
                    resetKey={currentStep}
                />
            </div>
            <ModalActions>
                <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                    {!isFirstStep && (
                        <Button
                            appearance="tertiary"
                            onClick={previousStep}
                            gaTrackingId="643758789423"
                            gaTrackingValue="Back"
                        >
                            Back
                        </Button>
                    )}
                    {!isResults && (
                        <Button
                            onClick={nextStep}
                            isDisabled={!canProceed}
                            gaTrackingId="643758789423"
                            gaTrackingValue={nextGaTrackingValue}
                        >
                            {currentStep === 'disclaimer' ? 'Continue' : 'Next'}
                        </Button>
                    )}
                </ButtonGroup>
            </ModalActions>
        </StyledModal>
    );
}

export default WizardModal;
